// File: src/contexts/DetailViewContext.js
// Copyright (c) 2025 DefoAI UG (haftungsbeschraenkt)
// All rights reserved.
//
// React context for propagating edit state to child tabs in detail views.
// Provides a standardized way for child components to access and modify form data.

import React, { createContext, useContext } from 'react';

/**
 * Default context values
 */
const defaultContextValue = {
    isEditing: false,
    isSaving: false,
    formData: {},
    validationErrors: {},
    updateFormData: () => {},
    setFieldValue: () => {},
};

/**
 * DetailViewContext - Context for propagating edit state to child components
 */
const DetailViewContext = createContext(defaultContextValue);

/**
 * DetailViewProvider - Provider component for DetailViewContext
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 * @param {boolean} props.isEditing - Whether currently in edit mode
 * @param {boolean} props.isSaving - Whether save operation is in progress
 * @param {Object} props.formData - Current form data
 * @param {Object} props.validationErrors - Current validation errors
 * @param {function} props.updateFormData - Function to update form data (partial updates)
 * @param {function} props.setFieldValue - Function to set a single field value
 */
const DetailViewProvider = ({
    children,
    isEditing,
    isSaving,
    formData,
    validationErrors,
    updateFormData,
    setFieldValue,
}) => {
    const value = {
        isEditing,
        isSaving,
        formData,
        validationErrors,
        updateFormData,
        setFieldValue,
    };

    return (
        <DetailViewContext.Provider value={value}>
            {children}
        </DetailViewContext.Provider>
    );
};

/**
 * useDetailViewContext - Hook to access DetailViewContext
 * @throws {Error} If used outside of DetailViewProvider
 * @returns {Object} Context value with edit state and actions
 */
const useDetailViewContext = () => {
    const context = useContext(DetailViewContext);

    if (context === undefined) {
        throw new Error('useDetailViewContext must be used within a DetailViewProvider');
    }

    return context;
};

export { DetailViewContext, DetailViewProvider, useDetailViewContext };
